import { trpc } from '@tencent/tpro_rmonitor_web_server_issue_list';
import { get } from 'lodash-es';
import * as uuid from 'uuid';
import AbstractHttpClient from '@/infrastructure/axios/request';
import { enumTrpc } from '@/proto/generated/tpro_rmonitor_web_server_issue_list';
import { IFlameGraph } from '@/service/lag-problem';
import { TrpcService } from '@/service/trpc';
import { WithTypeCast } from '@raft/utils';

type IGetTopIssuesReq = WithTypeCast<trpc.rmonitor.web_server.IGetTopIssuesReq>;
type ITopIssueInfo = WithTypeCast<trpc.rmonitor.web_server.ITopIssueInfo>;
type ISubIssuesPollReq = WithTypeCast<trpc.rmonitor.web_server.ISubIssuesPollReq>;
type ISubIssuesPollRsp = WithTypeCast<trpc.rmonitor.web_server.ISubIssuesPollRsp>;
type ISubIssuesTaskReq = WithTypeCast<trpc.rmonitor.web_server.ISubIssuesTaskReq>;
type ISubIssuesTaskRsp = WithTypeCast<trpc.rmonitor.web_server.ISubIssuesTaskRsp>;
type IGetIssueListReq = WithTypeCast<trpc.rmonitor.web_server.IGetIssueListReq>;
type IGetIssueListRsp = WithTypeCast<trpc.rmonitor.web_server.IGetIssueListRsp>;
export type IGetTreeReq = WithTypeCast<trpc.rmonitor.web_server.IGetTreeReq>;
export type IGetTreeRsp = WithTypeCast<trpc.rmonitor.web_server.IGetTreeRsp>;
export type IRecommendedIssueListReq = WithTypeCast<trpc.rmonitor.web_server.IRecommendedIssueListReq>;
export type IRecommendedIssueListRsp = WithTypeCast<trpc.rmonitor.web_server.IRecommendedIssueListRsp>;
export type IRecommendedIssueInfo = WithTypeCast<trpc.rmonitor.web_server.IRecommendedIssueInfo>;
export type IIssueInfo = WithTypeCast<trpc.rmonitor.web_server.IIssueInfo>;
export type IICommonInfo =  WithTypeCast<trpc.rmonitor.web_server.ICommonInfo>;

type IStackTree = WithTypeCast<trpc.rmonitor.web_server.IStackTree>;
export const { StackType } = enumTrpc.rmonitor.web_server;
export type StackType = enumTrpc.rmonitor.web_server.StackType;

export interface ITreeStackTrace extends IStackTree {
  id?: string;
  previousLevelCount?: number | null; // 父节点count
  methods?: string[];
  rootNodeCount?: number | null; // 根节点count
  tableChildren?: ITreeStackTrace[];
  isTableChildrenEnd?: boolean; // 是否为当前Children的最后一条数据
  previousRate?: string | null;// 对父节点影响占比
  lineKey?: string; // 用于表格展开时的折线展示
  flameGraphWidth?: number | null; // 更改需求,为了更美观，不需要严格按照等比展示,在火焰图绘制宽度替代duration,
  children?: IFlameGraph[] | null;
}

export interface IRsp {
  tree: ITreeStackTrace;
  allCount?: number |null;
  useCount?: number | null;
}

export const isDummyMethod = (tree?: ITreeStackTrace | null) => tree?.method === 'dummyMethod';

const treeStackAddId = (tree: IStackTree): ITreeStackTrace => {
  const recur = (
    tree: IStackTree,
    rootNode: ITreeStackTrace,
    previousLevel?: ITreeStackTrace,
  ): ITreeStackTrace => {
    if (!tree) {
      return tree;
    }

    const newTree: ITreeStackTrace = {
      ...tree,
      id: uuid.v4(),
      previousLevelCount: previousLevel?.count,
      rootNodeCount: rootNode.count,
      previousRate: previousLevel?.count ? (((tree.count || 0) / (previousLevel?.count || 0)) * 100).toFixed(2) : '0',
    };

    if (newTree?.children) {
      newTree.children = newTree.children.map(((tree: IStackTree) => recur(tree, rootNode, newTree)));
    }
    if (newTree?.children?.length) {
      newTree.flameGraphWidth = newTree?.children?.reduce?.(
        (accumulator, currentValue) => accumulator + (currentValue?.flameGraphWidth || 0),
        0,
      );
    } else {
      newTree.flameGraphWidth = 0;
    }
    return newTree;
  };
  return recur(tree, tree, tree);
};

/**
 * 产品成员管理
 */
export class IssueService extends AbstractHttpClient {
  constructor() {
    super({
      serviceName: TrpcService.IssueListService,
    });
  }

  /**
   * 获取所有角色
   */
  public async getTopIssues(req: IGetTopIssuesReq): Promise<ITopIssueInfo[]> {
    try {
      const rsp = await this.trpcPost('GetTopIssues', req);
      return get(rsp, 'issues', []);
    } catch (e) {
      return [];
    }
  }


  /**
   * 查询二次聚类之后的问题列表
   */
  public async subIssuesPoll(req: ISubIssuesPollReq): Promise<ISubIssuesPollRsp> {
    try {
      return await this.trpcPost<ISubIssuesPollRsp>('SubIssuesPoll', req) || {};
    } catch (e) {
      return {};
    }
  };

  /**
   * 开始拆分
   */
  public async subIssuesTask(req: ISubIssuesTaskReq): Promise<string | null> {
    try {
      const res = await this.trpcPost<ISubIssuesTaskRsp>('SubIssuesTask', req) || {};
      return res?.taskId ?? null;
    } catch (e) {
      return null;
    }
  };

  public async getIssueList(req: IGetIssueListReq): Promise<IGetIssueListRsp> {
    try {
      return await this.trpcPost<ISubIssuesTaskRsp>('GetIssueList', req);
    } catch (e) {
      return {
        total: 0,
        issueList: [],
      };
    }
  }

  public async getRecommendIssueList(req: IRecommendedIssueListReq): Promise<IRecommendedIssueInfo[]> {
    try {
      const res = await this.trpcPost<IRecommendedIssueListRsp>('RecommendedIssueList', req);
      return res.recommendedIssueInfo ?? [];
    } catch (e) {
      return [];
    }
  }

  public async getTree(req: IGetTreeReq): Promise<IRsp> {
    try {
      const rsp =  await this.trpcPost<IGetTreeRsp>('GetTree', req);
      if (isDummyMethod(rsp.tree)) {
        const count = rsp?.tree?.children?.reduce?.(
          (accumulator: any, currentValue: any) => accumulator + (currentValue?.count || 0),
          0,
        );
        return {
          tree: treeStackAddId({ ...rsp.tree, count }),
          allCount: rsp.allCount,
          useCount: rsp.useCount } ;
      }
      return {
        tree: treeStackAddId(rsp.tree || {}),
        allCount: rsp.allCount,
        useCount: rsp.useCount,
      };
    } catch (e) {
      return { tree: {}, allCount: 0, useCount: 0 };
    }
  }
}


export const issueService = new IssueService();
